import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Check, X, Star, ShieldCheck, Clock, AlertCircle, Mail, Phone, Globe } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "pending",  label: "En attente",  icon: Clock },
  { key: "approved", label: "Approuvés",   icon: ShieldCheck },
  { key: "rejected", label: "Refusés",     icon: AlertCircle },
];

export default function AdminTechnicians() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(`/admin/technicians?status=${tab}`)
      .then(({ data }) => setItems(data.items || []))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [tab]);

  const act = async (id, action, payload) => {
    try {
      await api.post(`/admin/technicians/${id}/${action}`, payload || {});
      toast.success(action === "approve" ? "Fiche approuvée" : action === "reject" ? "Fiche refusée" : "Vedette basculée");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-12" data-testid="admin-technicians-page">
      <div className="eyebrow">Modération</div>
      <h1 className="mt-1 font-display text-3xl font-black tracking-tight">Techniciens (admin)</h1>

      <div className="mt-8 flex flex-wrap gap-2 border-b border-black/10">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
            className={`inline-flex items-center gap-2 px-4 py-3 -mb-px border-b-2 font-medium text-sm transition-colors ${
              tab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <Card className="mt-8 p-10 border-black/10 text-center text-sm text-muted-foreground">
          Aucune fiche dans cette catégorie.
        </Card>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-4">
          {items.map((t) => <AdminTechCard key={t.id} t={t} onAct={act} />)}
        </div>
      )}
    </div>
  );
}

function AdminTechCard({ t, onAct }) {
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  return (
    <Card className="p-6 border-black/10" data-testid={`admin-tech-${t.id}`}>
      <div className="flex flex-col md:flex-row md:items-start gap-6">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-lg font-semibold">{t.company_name}</h3>
            {t.featured && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                <Star className="h-3 w-3" /> Vedette
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t.contact_name && `${t.contact_name} · `}{t.city} · {t.postal_code}{t.department && ` (${t.department})`}
          </p>
          <p className="mt-3 text-sm text-foreground leading-relaxed">{t.description || "Aucune description."}</p>
          {t.brands_supported?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {t.brands_supported.map((b) => (
                <span key={b} className="rounded-full bg-muted text-muted-foreground px-2 py-0.5 text-[10px] uppercase tracking-wider">
                  {b}
                </span>
              ))}
            </div>
          )}
          {t.certifications && <p className="mt-2 text-xs italic text-muted-foreground">{t.certifications}</p>}

          <div className="mt-4 flex flex-wrap gap-4 text-xs text-muted-foreground">
            {t.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {t.email}</span>}
            {t.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {t.phone}</span>}
            {t.website && (
              <a href={t.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                <Globe className="h-3 w-3" /> {t.website}
              </a>
            )}
            <span>Rayon {t.service_radius_km} km</span>
            <span>Lat {t.latitude ?? "—"} · Lng {t.longitude ?? "—"}</span>
          </div>
          {t.reject_reason && (
            <div className="mt-3 text-xs rounded-lg bg-red-50 text-red-800 border border-red-200 px-3 py-2">
              <span className="font-semibold">Motif de refus :</span> {t.reject_reason}
            </div>
          )}
        </div>

        <div className="w-full md:w-64 space-y-2">
          {t.status !== "approved" && (
            <Button onClick={() => onAct(t.id, "approve")} className="w-full rounded-md" data-testid={`btn-approve-${t.id}`}>
              <Check className="h-4 w-4 mr-2" /> Approuver
            </Button>
          )}
          {t.status === "approved" && (
            <Button onClick={() => onAct(t.id, "toggle-featured")} variant="outline" className="w-full rounded-md" data-testid={`btn-featured-${t.id}`}>
              <Star className="h-4 w-4 mr-2" /> {t.featured ? "Retirer vedette" : "Mettre en vedette"}
            </Button>
          )}
          {t.status !== "rejected" && (
            <>
              {rejecting ? (
                <div className="space-y-2">
                  <Input
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Motif du refus (visible par le pro)"
                    className="text-xs"
                    data-testid={`reject-reason-${t.id}`}
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={() => { onAct(t.id, "reject", { reason: rejectReason }); setRejecting(false); setRejectReason(""); }}
                      variant="destructive"
                      className="flex-1 rounded-md"
                      data-testid={`btn-reject-confirm-${t.id}`}
                    >
                      Confirmer
                    </Button>
                    <Button onClick={() => setRejecting(false)} variant="ghost" className="rounded-md">Annuler</Button>
                  </div>
                </div>
              ) : (
                <Button onClick={() => setRejecting(true)} variant="outline" className="w-full rounded-md text-destructive border-destructive/30 hover:bg-destructive/5" data-testid={`btn-reject-${t.id}`}>
                  <X className="h-4 w-4 mr-2" /> Refuser
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
